import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg, TIPOS_CITA, ESTADOS_CITA, FRECUENCIAS, waLink, fmtDate } from "@/lib/api";
import { CitaTto } from "@/components/CitaTto";
import { ApptEco } from "@/components/ApptEco";
import { ProximasCitas } from "@/components/ProximasCitas";
import { avisarCambio } from "@/lib/avisoProf";

const L = ({ t, children }) => (
  <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">{t}<div className="mt-1">{children}</div></label>
);

export const PatientPicker = ({ value, label, onPick, centro = "" }) => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [creando, setCreando] = useState(false);
  useEffect(() => {
    if (q.length < 2) return setRes([]);
    const t = setTimeout(() => api.get("/patients", { params: { q } }).then((r) => setRes(r.data.slice(0, 8))), 200);
    return () => clearTimeout(t);
  }, [q]);
  const crear = async () => {
    const nombre = q.trim();
    const { data: dups } = await api.get("/patients/duplicados", { params: { nombre } }).catch(() => ({ data: [] }));
    const aviso = dups.length
      ? `OJO: ya hay ${dups.length} paciente(s) con un nombre parecido:\n${dups.map((d) => `• ${d.nombre}${d.centro ? ` (${d.centro})` : ""}${d.telefono ? ` · ${d.telefono}` : ""}`).join("\n")}\n\nSi es uno de ellos, pulsa Cancelar y elígelo en la lista. ¿Crear igualmente «${nombre}» como paciente nuevo?`
      : `¿Crear el paciente «${nombre}»? Luego puedes completar su ficha.`;
    if (!window.confirm(aviso)) return;
    setCreando(true);
    try {
      const { data } = await api.post("/patients", { nombre, centro });
      toast.success(`Paciente ${data.patient.nombre} creado`);
      onPick(data.patient);
      setQ("");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setCreando(false);
  };
  return (
    <div className="relative">
      <Input data-testid="appt-patient-search" placeholder={value ? label : "Buscar paciente (opcional)…"} value={q} onChange={(e) => setQ(e.target.value)} />
      {value && !q && <div className="text-xs text-slate-600 mt-1">Paciente: <b>{label}</b> <button type="button" className="text-rose-600 ml-1" onClick={() => onPick(null)}>quitar</button></div>}
      {q.trim().length >= 2 && (
        <div className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg max-h-64 overflow-y-auto">
          {res.map((p) => (
            <button type="button" key={p.id} data-testid={`appt-patient-${p.id}`} onClick={() => { onPick(p); setQ(""); }} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50">
              {p.nombre} <span className="text-xs text-slate-400">{p.codigo ? `Nº ${p.codigo} · ` : ""}{p.centro}</span>
            </button>
          ))}
          <button type="button" disabled={creando} onClick={crear} data-testid="appt-patient-crear" className="w-full text-left px-3 py-2 text-sm font-medium text-[#1E3A8A] border-t hover:bg-sky-50">
            + Crear paciente nuevo «{q.trim()}»
          </button>
        </div>
      )}
    </div>
  );
};

const CENTRO_TXT = (c) => (c === "MADRID" ? "Madrid" : c ? "Alcobendas" : "");
let TEXTOS = {};
let CENTROS_R = {};
export const setTextosRecordatorio = (d) => { TEXTOS = d?.textos || {}; CENTROS_R = d?.centros || {}; };
export const cargarTextosRecordatorio = () => api.get("/recordatorio-textos").then((r) => setTextosRecordatorio(r.data));
export const TEXTO_BASE = "Hola, le recordamos su cita de {tipo} para {paciente} el {fecha} a las {hora} con {profesional} en nuestro centro de {centro} ({direccion}). Si no puede venir, avísenos por favor en el {telefono_centro}. Gracias. Salud y Visión";
export const rellenarTexto = (t, a, prof, centros = CENTROS_R) => t
  .replaceAll("{tipo}", TIPOS_CITA[a.tipo]?.label.toLowerCase() || "")
  .replaceAll("{paciente}", a.patient?.nombre || "")
  .replaceAll("{fecha}", fmtDate(a.fecha))
  .replaceAll("{hora}", a.inicio || "")
  .replaceAll("{profesional}", prof?.nombre || "")
  .replaceAll("{centro}", CENTRO_TXT(a.patient?.centro))
  .replaceAll("{direccion}", centros[a.patient?.centro]?.direccion || "")
  .replaceAll("{telefono_centro}", centros[a.patient?.centro]?.telefono || "");

export const recordatorioMsg = (a, prof) => (TEXTOS[a.tipo] || (CENTROS_R[a.patient?.centro] && prof) ? rellenarTexto(TEXTOS[a.tipo] || TEXTO_BASE, a, prof) : `Hola, le recordamos su cita de ${TIPOS_CITA[a.tipo]?.label.toLowerCase()}${a.patient?.nombre ? ` para ${a.patient.nombre}` : ""} el ${fmtDate(a.fecha)} a las ${a.inicio}${prof ? ` con ${prof.nombre}` : ""}${a.patient?.centro ? ` en nuestro centro de ${CENTRO_TXT(a.patient.centro)}` : ""}. Si no puede venir, avísenos por favor. Gracias. Salud y Visión`);

const plus3m = (f) => { const d = new Date(`${f}T12:00`); d.setMonth(d.getMonth() + 3); return d.toISOString().slice(0, 10); };

const Repetir = ({ f, setF, existente }) => (
  <div className="col-span-2 grid grid-cols-2 gap-3 rounded-md border border-dashed border-slate-300 p-2" data-testid="appt-repetir">
    <L t={existente ? "Repetir a partir de esta cita" : "Repetir"}><select data-testid="appt-frecuencia" className="field-select" value={f.cada || 0} onChange={(e) => setF({ ...f, cada: Number(e.target.value), hasta: f.hasta || plus3m(f.fecha) })}>
      {FRECUENCIAS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
    </select></L>
    {f.cada > 0 && <L t="Hasta el día"><Input data-testid="appt-hasta" type="date" value={f.hasta} onChange={(e) => setF({ ...f, hasta: e.target.value })} /></L>}
  </div>
);

export const ApptDialog = ({ appt, profs, onClose, onSaved, onEditHueco }) => {
  const [f, setF] = useState(null);
  useEffect(() => {
    setF(appt ? { tipo: "otro", estado: "programada", titulo: "", notas: "", patient_id: null, ...appt } : null);
  }, [appt]);
  if (!f) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const prof = profs.find((p) => p.id === f.profesional_id);
  const tel = f.patient?.telefono;
  const msg = recordatorioMsg(f, prof);

  const save = async () => {
    try {
      const { hueco, ...rest } = f;
      const body = { ...rest, patient_id: f.patient?.id || f.patient_id || null };
      if (f.cada > 0 && (!f.id || !f.serie_id)) {
        if (f.id) {
          await api.put(`/appointments/${f.id}`, body);
          const d = new Date(`${f.fecha}T12:00`);
          d.setDate(d.getDate() + 7 * f.cada);
          body.fecha = d.toISOString().slice(0, 10);
          delete body.id;
          delete body.estado;
          delete body.verificada;
        }
        const { data } = await api.post("/appointments/serie", { cita: body, cada_semanas: f.cada, hasta: f.hasta, desde_cita: f.id || null });
        const avisos = [data.conflictos.length && `${data.conflictos.length} con el hueco ocupado (${data.conflictos.map(fmtDate).join(", ")})`, data.festivos.length && `${data.festivos.length} en festivo`].filter(Boolean);
        toast[avisos.length ? "warning" : "success"](`${data.creadas.length} citas creadas${avisos.length ? `. No se han creado: ${avisos.join(" y ")}. Ponlas a mano.` : ""}`, { duration: avisos.length ? 12000 : 4000 });
        return onSaved();
      }
      if (f.id) await api.put(`/appointments/${f.id}`, body);
      else await api.post("/appointments", body);
      toast.success("Cita guardada");
      if (f.id) avisarCambio({ profs, orig: appt, nuevo: body, accion: body.estado === "anulada" && appt.estado !== "anulada" ? "anulada" : "cambio" });
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async () => {
    let siguientes = false;
    if (f.serie_id) {
      const r = window.prompt("Esta cita se repite. Escribe 1 para borrar solo esta, o 2 para borrar esta y todas las siguientes de la serie.", "1");
      if (r !== "1" && r !== "2") return;
      siguientes = r === "2";
    } else if (!window.confirm("¿Eliminar esta cita?")) return;
    try {
      const { data } = await api.delete(`/appointments/${f.id}`, { params: { siguientes } });
      toast.success(data.borradas > 1 ? `${data.borradas} citas eliminadas` : "Cita eliminada", {
        duration: 10000,
        action: data.deshacer_id && {
          label: "Deshacer",
          onClick: async () => {
            try {
              const r = await api.post(`/appointments/restaurar/${data.deshacer_id}`);
              toast.success(r.data.restauradas > 1 ? `${r.data.restauradas} citas recuperadas` : "Cita recuperada");
            } catch (e) {
              toast.error(errMsg(e));
            }
            onSaved();
          },
        },
      });
      avisarCambio({ profs, orig: appt, accion: "borrada" });
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Dialog open={!!appt} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="appt-dialog">
        <DialogHeader><DialogTitle className="font-[Outfit]">{f.id ? "Editar cita" : "Nueva cita"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><L t="Paciente"><PatientPicker value={f.patient?.id || f.patient_id} label={f.patient?.nombre} centro={prof?.centro || ""} onPick={(p) => setF({ ...f, patient: p, patient_id: p?.id || null })} /></L></div>
          {f.patient && (
            <div className="col-span-2 -mt-1 flex flex-wrap items-center gap-2 rounded-md bg-slate-50 px-3 py-2 text-xs" data-testid="appt-patient-info">
              <span className="rounded bg-white border px-1.5 py-0.5 font-semibold text-slate-700" data-testid="appt-ficha">Ficha nº {f.patient.codigo || "—"}</span>
              <span className="rounded px-1.5 py-0.5 font-semibold text-white" style={{ background: TIPOS_CITA[f.tipo]?.color || "#64748B" }} data-testid="appt-tipo-badge">{TIPOS_CITA[f.tipo]?.label}</span>
              {f.tto_info && <span className="text-slate-600">{f.tto_info}</span>}
              <Link to={`/pacientes/${f.patient.id}`} className="ml-auto text-[#1E3A8A] underline" data-testid="appt-ver-ficha">Ver ficha</Link>
            </div>
          )}
          <div className="col-span-2"><L t="Título / nota corta"><Input data-testid="appt-titulo" value={f.titulo} onChange={set("titulo")} placeholder="Ej.: HUECO LIBRE, reunión…" /></L></div>
          <L t="Tipo"><select data-testid="appt-tipo" className="field-select" value={f.tipo} onChange={set("tipo")}>{Object.entries(TIPOS_CITA).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></L>
          <L t="Estado"><select data-testid="appt-estado" className="field-select" value={f.estado} onChange={set("estado")}>{Object.entries(ESTADOS_CITA).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></L>
          <L t="Profesional"><select data-testid="appt-prof" className="field-select" value={f.profesional_id} onChange={set("profesional_id")}>{profs.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}</select></L>
          <L t="Fecha"><Input data-testid="appt-fecha" type="date" value={f.fecha} onChange={set("fecha")} /></L>
          <L t="Inicio"><Input data-testid="appt-inicio" type="time" step="900" value={f.inicio} onChange={set("inicio")} /></L>
          <L t="Fin"><Input data-testid="appt-fin" type="time" step="900" value={f.fin} onChange={set("fin")} /></L>
          <CitaTto f={f} setF={setF} />
          <div className="col-span-2"><L t="Notas"><Textarea data-testid="appt-notas" rows={2} value={f.notas} onChange={set("notas")} /></L></div>
          {(!f.id || !f.serie_id) && <Repetir f={f} setF={setF} existente={!!f.id} />}
          {f.serie_id && <p className="col-span-2 text-[11px] text-slate-500" data-testid="appt-serie-info">Esta cita forma parte de una serie que se repite.</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {f.id && <Button variant="outline" data-testid="appt-delete" onClick={remove} className="text-rose-700"><Trash2 className="h-4 w-4" /></Button>}
          {f.patient && (tel ? (
            <a data-testid="appt-whatsapp" href={waLink(tel, msg)} target="_blank" rel="noreferrer" onClick={() => f.id && api.post(`/appointments/${f.id}/recordado`)} className="inline-flex items-center gap-1 rounded-md bg-[#25D366] text-white text-sm px-3 h-9 hover:opacity-90"><MessageCircle className="h-4 w-4" />Recordar cita por WhatsApp</a>
          ) : (
            <span className="text-xs text-slate-400">Sin teléfono · <Link className="underline" to={`/pacientes/${f.patient.id}`}>añadir en ficha</Link></span>
          ))}
          {f.patient && <ApptEco patient={f.patient} />}
          {f.patient && <ProximasCitas patient={f.patient} />}
          {f.hueco && onEditHueco && <Button variant="outline" data-testid="appt-editar-hueco" onClick={() => onEditHueco(f.hueco)}>Cambiar el hueco solo este día</Button>}
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button data-testid="appt-save" onClick={save} className="bg-[#1E3A8A] hover:bg-[#1E40AF]">Guardar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
